import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Button, Row, FlowGrid } from '@fs/zion-ui'

/**
 * SuggestionButtons
 *
 * A generic component that renders a row of suggestion buttons with configurable behavior.
 * Integrates with useFieldArray from zion-form to add items to form arrays.
 *
 * Features:
 * - Configurable data factory functions for each button
 * - Dynamic visibility controlled by parent via isVisible() functions
 * - Optional instruction text/content rendering above buttons
 * - Fully reusable across different cards and use cases
 *
 * @component
 * @example
 * ```javascript
 * <SuggestionButtons
 *   suggestions={[
 *     {
 *       id: 'birth',
 *       label: 'BIRTH',
 *       icon: ContentAdd,
 *       createData: () => createEventWithType('BIRTH', types),
 *       isVisible: () => !events.some(e => e.eventType?.key === 'BIRTH'),
 *       testId: 'add-birth-event',
 *       linkName: 'events-add-birth'
 *     }
 *   ]}
 *   onSuggestionClick={(data) => append(data)}
 * />
 * ```
 */
const SuggestionButtons = ({ suggestions, onSuggestionClick }) => {
  /**
   * Handles button click: calls the createData factory and invokes the callback
   */
  const handleClick = useCallback(
    (suggestion) => {
      try {
        // Call the factory function to create the data
        const data = suggestion.createData()

        // Call the parent's callback with the created data
        onSuggestionClick({ ...data, isOtherEvent: suggestion.id === 'other' })
      } catch (error) {
        console.error(`Error in SuggestionButtons: Failed to create data for suggestion "${suggestion.id}"`, error)
      }
    },
    [onSuggestionClick]
  )

  // Filter suggestions based on visibility check function
  const visibleSuggestions = suggestions.filter((suggestion) => {
    // If a visibility check function is provided, use it for dynamic filtering
    if (typeof suggestion.isVisible === 'function') {
      return suggestion.isVisible()
    }

    return true
  })

  // If no visible suggestions, render nothing
  if (visibleSuggestions.length === 0) {
    return null
  }

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="nano">
      {visibleSuggestions.length > 0 && (
        <Row wrap guttersX="xs" guttersY="pico">
          {visibleSuggestions.map((suggestion) => (
            <Button
              key={suggestion.id}
              Icon={suggestion.icon}
              emphasis="low"
              onClick={() => handleClick(suggestion)}
              data-testid={suggestion.testId || `suggestion-button-${suggestion.id}`}
              linkName={suggestion.linkName || `suggestion-${suggestion.id}`}
            >
              {suggestion.label}
            </Button>
          ))}
        </Row>
      )}
    </FlowGrid>
  )
}

SuggestionButtons.propTypes = {
  /**
   * Array of suggestion configurations. Each suggestion defines a button's appearance,
   * behavior, and the data it should create when clicked.
   */
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      /** Unique identifier for the button */
      id: PropTypes.string.isRequired,
      /** Display text for the button */
      label: PropTypes.string.isRequired,
      /** Zion icon component (e.g., ContentAdd) */
      icon: PropTypes.elementType,
      /** Factory function that returns the data to append when button is clicked */
      createData: PropTypes.func.isRequired,
      /** Optional function to dynamically determine if button should be visible */
      isVisible: PropTypes.func,
      /** Optional data-testid for the button */
      testId: PropTypes.string,
      /** Optional linkName for analytics */
      linkName: PropTypes.string,
    })
  ).isRequired,
  /**
   * Callback invoked with the result of createData() when a button is clicked.
   * Typically this is the append function from useFieldArray.
   */
  onSuggestionClick: PropTypes.func.isRequired,
}

export default SuggestionButtons
